import type { StepType } from '@poseidon/components/ContactDetail';

/**
 * The `?action=` values that the contact-detail "Action" editor can seed an
 * editable step from. `getActionStep` only knows how to build a step for these
 * four types; anything else yields `undefined`.
 *
 * IMPORTANT: the "Reply inline" flow (`onClickToRespond`) writes *interaction-
 * action* values — `message_received`, `email_received`, `inmail_received`,
 * `document_viewed` — into the SAME `?action=` URL param. None of those are
 * seedable step types, which is exactly why the seed must treat them as a no-op
 * (see `computeActionEditorSeed`) instead of looping.
 *
 * Keep this list in sync with the buildable branches of `getActionStep` in
 * `ContactDetailPageNextJs.tsx` — `satisfies readonly StepType[]` only checks
 * each entry is a real `StepType`, not that the list is complete.
 */
export const SEEDABLE_ACTION_STEP_TYPES = ['email', 'inmail', 'message', 'connect'] as const satisfies readonly StepType[];

export type SeedableActionStepType = (typeof SEEDABLE_ACTION_STEP_TYPES)[number];

/**
 * True only for the action values `getActionStep` can turn into an editable
 * step. Returns `false` for interaction-action values (`message_received`, …),
 * empty/absent input, and any other string.
 */
export function isSeedableActionStepType(action: string | null | undefined): action is SeedableActionStepType {
  return action != null && SEEDABLE_ACTION_STEP_TYPES.some((type) => type === action);
}

/**
 * Decide what the render-phase Action-editor seed should do, given the current
 * URL `action`, whether a step is already being edited, and a builder that turns
 * an action into a step (or `undefined` when it can't).
 *
 * Returns the step to seed, or `undefined` for "leave the editor untouched".
 *
 * CRITICAL — why this is a pure, separately-tested function: the caller runs it
 * during render and only calls `setState` on a truthy result. If it ever
 * returned a value for an action the builder can't handle, that render-phase
 * `setState` would re-fire on every render → React "Too many re-renders". That
 * is the exact infinite loop clicking "Reply inline" used to trigger, because it
 * seeds `?action=message_received`, which `buildActionStep` returns `undefined`
 * for. So this MUST return `undefined` whenever there is no action, a step is
 * already being edited, or the builder returns `undefined`.
 */
export function computeActionEditorSeed<TStep>(params: {
  action: string | null | undefined;
  actionEditingStep: TStep | null | undefined;
  buildActionStep: (action: string) => TStep | undefined;
}): TStep | undefined {
  const { action, actionEditingStep, buildActionStep } = params;
  // No action to seed from, or the user is already editing a step — leave it be.
  if (!action || actionEditingStep != null) return undefined;
  // Builder returns `undefined` for values it can't build (interaction-action
  // values, or before contact data has loaded) → no seed, no setState, no loop.
  return buildActionStep(action);
}
